import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = ["src", "alt"]
const _hoisted_2 = ["alt"]
const _hoisted_3 = { class: "game-details" }
const _hoisted_4 = { class: "draw-time" }
const _hoisted_5 = { class: "results" }
const _hoisted_6 = {
  key: 0,
  class: "cancelled-overlay"
}
const _hoisted_7 = ["alt"]
const _hoisted_8 = { class: "numbers-wrap" }
const _hoisted_9 = {
  key: 0,
  class: "action-section"
}
const _hoisted_10 = {
  key: 0,
  class: "countdown-box"
}
const _hoisted_11 = { class: "countdown-block" }
const _hoisted_12 = { class: "countdown-number" }
const _hoisted_13 = { class: "countdown-label" }
const _hoisted_14 = { class: "countdown-block" }
const _hoisted_15 = { class: "countdown-number" }
const _hoisted_16 = { class: "countdown-label" }
const _hoisted_17 = { class: "countdown-block" }
const _hoisted_18 = { class: "countdown-number" }
const _hoisted_19 = { class: "countdown-label" }

import { getGameLogoUrl, hasGameFlag, parseMainNumbers, parseBonusNumbers } from '@/utils/resultParser';
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
// Import cancelled SVG - using public path

const __default__ = {
  name: 'LotteryResultCard'
}


export default /*@__PURE__*/_defineComponent({
  ...__default__,
  props: {
    lottery: {}
  },
  setup(__props: any) {

const cancelledImg = '/img/cancelled.svg';

const router = useRouter();
const route = useRoute();
const hideBetAction = computed(() => route.meta.minimalChrome === true);

const props = __props

const mainNumbers = computed(() => parseMainNumbers(props.lottery?.result));
const bonusNumbers = computed(() => parseBonusNumbers(props.lottery?.result));

// Check if the result is cancelled (empty or null)
const isCancelled = computed(() => {
  const lottery = props.lottery;
  
  // Check explicit status first
  if (lottery?.status === 'cancelled') {
    return true;
  }
  
  const result = lottery?.result;
  
  // Check for various empty/null conditions
  if (!result || result === '' || result === null || result === undefined) {
    return true;
  }
  
  // Check if result is an empty array string
  if (result === '[]' || result === '[[]]' || result === '[[],[]]') {
    return true;
  }
  
  // Check if result is a stringified empty array
  try {
    const parsed = JSON.parse(result);
    if (Array.isArray(parsed)) {
      // Check if it's empty or contains only empty arrays
      if (parsed.length === 0) return true;
      if (parsed.every(arr => Array.isArray(arr) && arr.length === 0)) return true;
    }
  } catch (e) {
    // If parsing fails, treat as cancelled if it's just whitespace
    if (typeof result === 'string' && result.trim() === '') {
      return true;
    }
  }
  
  return false;
});

const formattedDrawTime = computed(() => {
  const drawDate = props.lottery?.draw_date;
  if (!drawDate) return '';

  const dateString = String(drawDate).trim();
  let date: Date;

  // Normalize and parse
  if (dateString.includes('+')) {
    // Example: "2025-07-29 23:51:30+00:00"
    const [datePart, offset] = dateString.split('+');
    date = new Date(datePart.replace(' ', 'T') + '+' + offset);
  } else if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateString)) {
    // "YYYY-MM-DD HH:mm:ss" → Assume UTC
    date = new Date(dateString.replace(' ', 'T') + 'Z');
  } else {
    date = new Date(dateString);
  }

  if (isNaN(date.getTime())) return '';

  // Format as "29/07/2025, 23:51:30" in local time
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`;
});


const isMobile = computed(() => window.matchMedia('(max-width: 768px)').matches)

const isResultsView = computed(() => {
  // Check if we're in the Results view by looking at the current route
  return window.location.pathname.includes('/results')
})

const truncatedName = computed(() => {
  const game_name = props.lottery?.game_name || ''
  if (isMobile.value) return game_name
  return game_name.length > 15 ? game_name.slice(0, 15) + '...' : game_name
})

// Navigation function
const navigateToDetail = () => {
  if (props.lottery && props.lottery.game_slug) {
    router.push(`/lottery/${props.lottery.game_slug}`);
  }
}

// Countdown logic
const countdown = ref({ hours: 0, minutes: 0, seconds: 0 })
let timer: number
const updateCountdown = () => {
  if (!props.lottery?.draw_date) {
    countdown.value = { hours: 0, minutes: 0, seconds: 0 }
    return
  }
  const now = new Date()
  
  // Parse the UTC date string properly for countdown
  const dateString = String(props.lottery.draw_date)
  let drawTime: Date
  
  if (dateString.includes('+')) {
    // UTC format like "2025-07-29 23:51:30+00:00"
    const datePart = dateString.split('+')[0]
    drawTime = new Date(datePart + 'Z')
  } else {
    // Fallback for other formats
    drawTime = new Date(dateString)
  }
  
  const diff = drawTime.getTime() - now.getTime()
  if (diff <= 0) {
    countdown.value = { hours: 0, minutes: 0, seconds: 0 }
    return
  }
  countdown.value = {
    hours: Math.floor(diff / (1000 * 60 * 60)),
    minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((diff % (1000 * 60)) / 1000)
  }
}
onMounted(() => {
  countdown.value = { hours: 0, minutes: 0, seconds: 0 }
  updateCountdown()
  timer = window.setInterval(updateCountdown, 1000)
})
onUnmounted(() => {
  if (timer) clearInterval(timer)
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["lottery-result-card", { 'embed-results': hideBetAction.value }])
  }, [
    _createElementVNode("div", {
      class: "flag-section",
      onClick: navigateToDetail
    }, [
      (_ctx.lottery && _unref(hasGameFlag)(_ctx.lottery.game_logo, _ctx.lottery.game_slug))
        ? (_openBlock(), _createElementBlock("img", {
            key: 0,
            src: _unref(getGameLogoUrl)(_ctx.lottery.game_logo, _ctx.lottery.game_slug),
            alt: `${_ctx.lottery.country} ${_ctx.$t('forms.flag')}`,
            class: "country-flag"
          }, null, 8, _hoisted_1))
        : (_openBlock(), _createElementBlock("img", {
            key: 1,
            src: "/img/flags/default.svg",
            alt: _ctx.$t('forms.defaultLogo'),
            class: "country-flag"
          }, null, 8, _hoisted_2))
    ]),
    _createElementVNode("div", {
      class: "content-section",
      onClick: navigateToDetail
    }, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("h3", null, _toDisplayString(_ctx.lottery?.game_name), 1),
        _createElementVNode("div", _hoisted_4, _toDisplayString(formattedDrawTime.value), 1)
      ]),
      _createElementVNode("div", _hoisted_5, [
        (isCancelled.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _createElementVNode("img", {
                src: cancelledImg,
                alt: _ctx.$t('wagers.cancelled'),
                class: "cancelled-icon"
              }, null, 8, _hoisted_7)
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_8, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(mainNumbers.value, (number) => {
            return (_openBlock(), _createElementBlock("div", {
              key: 'main-' + number,
              class: "number-ball"
            }, _toDisplayString(number), 1))
          }), 128)),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(bonusNumbers.value, (number) => {
            return (_openBlock(), _createElementBlock("div", {
              key: 'bonus-' + number,
              class: "number-ball bonus"
            }, _toDisplayString(number), 1))
          }), 128))
        ])
      ])
    ]),
    (!hideBetAction.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
          (isMobile.value && !isResultsView.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                _createElementVNode("div", _hoisted_11, [
                  _createElementVNode("div", _hoisted_12, _toDisplayString(String(countdown.value.hours).padStart(2, '0')), 1),
                  _createElementVNode("div", _hoisted_13, _toDisplayString(_ctx.$t('time.hours')), 1)
                ]),
                _createElementVNode("div", _hoisted_14, [
                  _createElementVNode("div", _hoisted_15, _toDisplayString(String(countdown.value.minutes).padStart(2, '0')), 1),
                  _createElementVNode("div", _hoisted_16, _toDisplayString(_ctx.$t('time.mins')), 1)
                ]),
                _createElementVNode("div", _hoisted_17, [
                  _createElementVNode("div", _hoisted_18, _toDisplayString(String(countdown.value.seconds).padStart(2, '0')), 1),
                  _createElementVNode("div", _hoisted_19, _toDisplayString(_ctx.$t('time.secs')), 1)
                ])
              ]))
            : _createCommentVNode("", true),
          (!isMobile.value || isResultsView.value)
            ? (_openBlock(), _createElementBlock("button", {
                key: 1,
                class: "bet-button",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('bet')))
              }, _toDisplayString(_ctx.$t('results.betOnNextDraw')), 1))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})